/**
 * Okta → Auth0 daisy-chain env resolution.
 *
 * Each workload env has a dedicated Okta OIDC IdP ("Tempus Auth0 Providers - {Env}") pointing at
 * that env's Auth0 custom domain. account-ui sends the IdP as an `idp` hint on /authorize so the
 * shared Okta org routes to the correct Auth0 tenant.
 *
 * Two separate `prompt=login` settings are in play: the one account-ui sends on Okta's /authorize,
 * and one configured in Okta admin on the IdP's own authorization endpoint (e.g.
 * login.stagingtempus.com/authorize?prompt=login) that forces re-auth at Auth0.
 */
import { getOauthBrowsingContextOrigin } from './okta-config';

/** Set on return URIs we build, marking an arrival as having already traversed Auth0. */
const AUTH0_DAISY_CHAIN_COMPLETED_PARAM = 'auth0Chained';

const INTERNAL_EMAIL_DOMAIN = '@tempus.com';

export function normalizeLoginEmail(value) {
  const trimmed = value.trim();
  if (!trimmed || trimmed.includes('@')) {
    return trimmed;
  }
  return `${trimmed}${INTERNAL_EMAIL_DOMAIN}`;
}

const AUTH0_DAISY_CHAIN_SUBMIT_BOUND = 'data-auth0-daisy-chain-bound';

/**
 * Excluded: EHR launches route through the institution's own IdP, and password set/reset and
 * activation own their own submit — redirecting those replaces Okta's form and breaks the flow.
 *
 * @param {{ fromEHR?: string; recoveryToken?: string; activationToken?: string; idp?: string }} queryObj
 * @param {{ auth0?: { oktaIdpId?: string } }} config
 * @returns {string | null}
 */
export function resolveAuth0DaisyChainIdpHint(queryObj, config) {
  if (queryObj.fromEHR || queryObj.recoveryToken || queryObj.activationToken) {
    return null;
  }

  // Explicit ?idp= override, for manual smoke tests against an IdP that isn't the env's configured one.
  if (queryObj.idp) {
    return queryObj.idp;
  }

  return config.auth0?.oktaIdpId || null;
}

/**
 * `fromLogin` marks a return from an SSO round trip, but SP-initiated and third-party returns carry
 * it without ever reaching Auth0 — including when Okta tokens are already readable. Send those
 * through the chain once; arrivals we built ourselves carry the completed marker and are left alone.
 *
 * @param {{ fromEHR?: string; fromLogin?: string; idp?: string; auth0Chained?: string }} queryObj
 * @param {{ auth0?: { oktaIdpId?: string } }} config
 * @returns {string | null}
 */
export function resolveAuth0DaisyChainRetryIdpHint(queryObj, config) {
  if (!queryObj.fromLogin || queryObj[AUTH0_DAISY_CHAIN_COMPLETED_PARAM]) {
    return null;
  }
  return resolveAuth0DaisyChainIdpHint(queryObj, config);
}

/** Return URI for a chain redirect: marked so the arrival back here is not sent through Auth0 again. */
function buildIdpRedirectOriginalUri() {
  const returnParams = new URLSearchParams(window.location.search.replace(/^\?/, ''));
  returnParams.delete('idp');
  returnParams.set('fromLogin', 'true');
  returnParams.set(AUTH0_DAISY_CHAIN_COMPLETED_PARAM, '1');
  const query = returnParams.toString();
  return `${getOauthBrowsingContextOrigin()}${window.location.pathname || '/'}${query ? `?${query}` : ''}`;
}

/**
 * /authorize params for every hop into the chain, so the two entry points can't drift apart.
 *
 * `prompt=login` because Okta answers /authorize from its own session when it has one, and an `idp`
 * hint does not override that — it only says where to send someone who still needs to authenticate.
 * Without it a session-holder is signed back in locally and never reaches Auth0.
 *
 * @param {{ idp: string; loginHint?: string | null }} params
 */
export function buildAuth0DaisyChainAuthorizeParams({ idp, loginHint }) {
  return {
    idp,
    ...(loginHint ? { loginHint } : {}),
    prompt: 'login',
    originalUri: buildIdpRedirectOriginalUri(),
  };
}

/**
 * Intercept IDP discovery "Next" and route to Auth0 via Okta `idp` + `loginHint` instead of
 * webfinger, which would otherwise resolve the user to Okta-local credentials. The typed email is
 * the hint: Okta's session endpoint is a cross-site request browsers block, and on a first login
 * there are no tokens to read one from either.
 *
 * @param {(email: string) => void} onSubmit called when the user enters an email and clicks Next
 * @returns {boolean} whether the handler was bound
 */
export function bindAuth0DaisyChainIdpDiscoverySubmit(onSubmit) {
  const submitButton = document.getElementById('idp-discovery-submit');
  if (!submitButton || submitButton.getAttribute(AUTH0_DAISY_CHAIN_SUBMIT_BOUND)) {
    return false;
  }
  submitButton.setAttribute(AUTH0_DAISY_CHAIN_SUBMIT_BOUND, 'true');

  const handleSubmit = (event) => {
    const inputElement = document.getElementById('idp-discovery-username');
    const normalizedEmail = normalizeLoginEmail(inputElement?.value ?? '');
    if (!normalizedEmail) {
      return;
    }
    if (inputElement && inputElement.value !== normalizedEmail) {
      inputElement.value = normalizedEmail;
      inputElement.dispatchEvent(new Event('input', { bubbles: true }));
    }
    if (normalizedEmail.toLowerCase().endsWith(INTERNAL_EMAIL_DOMAIN)) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    onSubmit(normalizedEmail);
  };

  submitButton.addEventListener('click', handleSubmit, true);
  const form = submitButton.closest('form');
  if (form) {
    form.addEventListener('submit', handleSubmit, true);
  }
  return true;
}
